import React, { useEffect, useState } from "react";
import { Loader2, RefreshCw, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { AirbyteConnection, useConnections, useSources, useDestinations, useSyncConnection, useDeleteConnection, useJobs } from "./airbyte-api";

// A sync job never settles the instant it's started — POST
// /{org}/connections/{id}/sync's own response is always "pending" — so the
// real progression to running/succeeded/failed only shows up by re-fetching
// GET /{org}/jobs, not from that one response. "pending"/"running" poll;
// anything else is a real end state.
const TERMINAL_JOB_STATUSES = new Set(["succeeded", "failed", "cancelled"]);

const JOB_STATUS_BADGE: Record<string, string> = {
  pending: "bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400",
  running: "bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400",
  succeeded: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400",
  failed: "bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-400",
  cancelled: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300",
};

const ConnectionRow: React.FC<{
  connection: AirbyteConnection;
  sourceName: string;
  destinationName: string;
  onDelete: (id: string, name: string) => void;
  deleting: boolean;
}> = ({ connection: c, sourceName, destinationName, onDelete, deleting }) => {
  const { sync } = useSyncConnection();
  // Scoped to just this connection — GET /{org}/jobs?connectionId= comes
  // back oldest-first (confirmed live), so the last element is the most
  // recent sync attempt, pending ones included.
  const { data: jobs, reload: reloadJobs } = useJobs(true, c.connectionId);
  const [syncing, setSyncing] = useState(false);
  const latestJob = jobs.length > 0 ? jobs[jobs.length - 1] : null;

  useEffect(() => {
    if (!latestJob || TERMINAL_JOB_STATUSES.has(latestJob.status)) return;
    const t = setTimeout(reloadJobs, 3000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestJob?.status, latestJob?.jobId]);

  const handleSync = async () => {
    setSyncing(true);
    const { result, alreadyRunning, error: syncError } = await sync(c.connectionId);
    setSyncing(false);
    if (result) {
      toast.success(`Sync started — job #${result.jobId} is ${result.status}.`);
      reloadJobs();
    } else {
      toast.error(alreadyRunning ? "A sync is already running for this connection." : syncError || "Couldn't start a sync.");
    }
  };

  return (
    <div className="flex items-center justify-between py-3">
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{c.name}</p>
        <p className="text-xs text-gray-400 dark:text-gray-500 flex items-center flex-wrap gap-x-1.5 gap-y-1 mt-0.5">
          <span>
            {sourceName} → {destinationName} · {c.status}
          </span>
          {latestJob && (
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium capitalize ${JOB_STATUS_BADGE[latestJob.status] || "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300"}`}>
              {!TERMINAL_JOB_STATUSES.has(latestJob.status) && <Loader2 size={9} className="animate-spin" />}
              Sync {latestJob.status}
            </span>
          )}
        </p>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <button
          onClick={handleSync}
          disabled={syncing}
          className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-500/30 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-500/10 disabled:opacity-50"
        >
          {syncing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Sync Now
        </button>
        <button
          onClick={() => onDelete(c.connectionId, c.name)}
          disabled={deleting}
          className="p-1.5 rounded-lg text-gray-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10 disabled:opacity-50"
          title="Delete connection"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
};

// Real GET /{org}/connections list — "Sync Now" calls POST
// /{org}/connections/{id}/sync directly (see api.md); each row's own
// GET /{org}/jobs?connectionId= (useJobs) shows that sync's real progress
// (pending -> running -> succeeded/failed), polled while non-terminal.
export const ConnectionsList: React.FC = () => {
  const navigate = useNavigate();
  const { data: connections, loading, error, reload } = useConnections(true);
  const { data: sources } = useSources(true);
  const { data: destinations } = useDestinations(true);
  const { remove: deleteConnection } = useDeleteConnection();
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Per direct ask: a failed load surfaces as a toast (the backend's own
  // message), not a persistent banner sitting above the rest of the page.
  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  const sourceName = (id: string) => sources.find((s) => s.sourceId === id)?.name || id;
  const destinationName = (id: string) => destinations.find((d) => d.destinationId === id)?.name || id;

  const handleDelete = async (connectionId: string, name: string) => {
    setDeletingId(connectionId);
    const { ok, error: deleteError } = await deleteConnection(connectionId);
    setDeletingId(null);
    if (ok) {
      toast.success(`${name} deleted.`);
      reload();
    } else {
      toast.error(deleteError || `Couldn't delete ${name}.`);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Your Connections</h2>
        <button
          onClick={() => navigate("/track/connections/new")}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg"
        >
          <Plus size={13} /> New Connection
        </button>
      </div>

      {loading && connections.length === 0 ? (
        <p className="flex items-center gap-1.5 text-sm text-gray-400 dark:text-gray-500 py-6">
          <Loader2 size={14} className="animate-spin" /> Loading connections…
        </p>
      ) : connections.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500 py-6 text-center">No connections yet — join a source to a destination to start syncing data.</p>
      ) : (
        <div className="divide-y divide-gray-100 dark:divide-gray-800">
          {connections.map((c) => (
            <ConnectionRow
              key={c.connectionId}
              connection={c}
              sourceName={sourceName(c.sourceId)}
              destinationName={destinationName(c.destinationId)}
              onDelete={handleDelete}
              deleting={deletingId === c.connectionId}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ConnectionsList;
